import { useEffect } from 'react';
import { useStore } from '@/store/useStore';
import { isLightboxOpen } from '@/utils/lightbox';

/** Возвращает текущее значение `currentDate` для CalendarPage через event-bus.
 *  Используем кастомные DOM-события, чтобы не таскать всё через store. */
export function fireKey(name: string, payload?: unknown) {
  window.dispatchEvent(new CustomEvent(name, { detail: payload }));
}

/** Фокус в поле ввода (в т.ч. выпадающий список) — горячие клавиши не срабатывают. */
export function isInputTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

export function useHotkeys() {
  const setModal = useStore((s) => s.setModal);
  const modal = useStore((s) => s.modal);
  const setPage = useStore((s) => s.setPage);
  const page = useStore((s) => s.page);

  useEffect(() => {
    const isInput = isInputTarget;

    const onKey = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;

      // Глобальные (работают всегда). Но если открыт полноэкранный просмотр фото —
      // Escape гасит только его (обрабатывает сам лайтбокс), карточку не закрываем.
      if (e.key === 'Escape') {
        if (isLightboxOpen()) return;
        if (modal) setModal(null);
        return;
      }
      if ((e.key === '?' || e.key === 'F1') && !isInput(e.target)) {
        e.preventDefault();
        setModal({ kind: 'help' });
        return;
      }

      if (isInput(e.target)) return;

      // Навигация по разделам: G then C/L/D/B
      if (e.key === '1' && meta) { e.preventDefault(); setPage('crm'); return; }
      if (e.key === '2' && meta) { e.preventDefault(); setPage('calendar'); return; }
      if (e.key === '3' && meta) { e.preventDefault(); setPage('dashboard'); return; }
      if (e.key === '4' && meta) { e.preventDefault(); setPage('directory'); return; }

      // Поиск
      if (meta && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        const inp = document.querySelector<HTMLInputElement>('input[placeholder^="Поиск"]');
        inp?.focus(); inp?.select();
        return;
      }

      // Новая сделка
      if (meta && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        setModal({ kind: 'deal', id: null });
        return;
      }

      // События для CalendarPage
      if (page === 'calendar') {
        // Стрелки листают дни только когда нет открытой модалки: при открытой карточке
        // ←/→ листают соседние карточки (обрабатывает DealEditor).
        if (e.key === 'ArrowLeft' && !modal) { e.preventDefault(); fireKey('cal:nav', { dir: -1 }); return; }
        if (e.key === 'ArrowRight' && !modal) { e.preventDefault(); fireKey('cal:nav', { dir: 1 }); return; }
        if (e.key === 'Home') { e.preventDefault(); fireKey('cal:today'); return; }
        if (e.key === 't' || e.key === 'T') { e.preventDefault(); fireKey('cal:today'); return; }
        if (e.key === 'd' || e.key === 'D') { e.preventDefault(); fireKey('cal:view', { view: 'day' }); return; }
        if (e.key === 'w' || e.key === 'W') { e.preventDefault(); fireKey('cal:view', { view: 'week' }); return; }
      }

      // Дублировать (если есть выделенная сделка) — отправим событие, страница подхватит
      if (meta && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        fireKey('deal:dup-current');
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal, page, setModal, setPage]);
}
